import React, { useState, useEffect, useCallback } from 'react';
import './ConfirmedTransactions.css';

const ConfirmedTransactions = ({ year, month }) => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [transactionType, setTransactionType] = useState('all'); // all, expenses, deposits
  const [expensePeriod, setExpensePeriod] = useState('last30Days');
  const [grantCategory, setGrantCategory] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [initialPeriodSet, setInitialPeriodSet] = useState(false);

  // Set up custom period from URL params on mount
  useEffect(() => {
    if (year && month && !initialPeriodSet) {
      const yearNum = parseInt(year);
      const monthNum = parseInt(month);
      // First day of the month
      const startDate = new Date(yearNum, monthNum - 1, 1);
      // Last day of the month
      const endDate = new Date(yearNum, monthNum, 0);

      setCustomStartDate(startDate.toISOString().split('T')[0]);
      setCustomEndDate(endDate.toISOString().split('T')[0]);
      setExpensePeriod('custom');
      setInitialPeriodSet(true);
    }
  }, [year, month, initialPeriodSet]);
  const [showCustomPeriodPopup, setShowCustomPeriodPopup] = useState(false);
  const [grantList, setGrantList] = useState([]);
  const [selectedGrantFilter, setSelectedGrantFilter] = useState('all');
  const [categories, setCategories] = useState([]);
  const [customCategories, setCustomCategories] = useState([]);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [editedGrantCategory, setEditedGrantCategory] = useState('');
  const [editedCustomizedCategory, setEditedCustomizedCategory] = useState('');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showEditDefaultPaymentPopup, setShowEditDefaultPaymentPopup] = useState(false);
  const [expandedItemRows, setExpandedItemRows] = useState({});

  const getLineItems = (transaction) => {
    try { return JSON.parse(transaction["Line Items"] || "[]"); } catch { return []; }
  };

  // Set up categories for both expenses and deposits
  const expenseCategories = ['Materials', 'Overhead', 'Travel', 'Fee', 'Equipment', 'Sub-Contract', 'Other (Grant-related)', 'Company Account Transfer', 'Credit Card Payment', 'N/A (Non-grant Item)'];
  const depositCategories = ['Revenue', 'Company Account Transfer', 'Shareholder Injection', 'PMS Payment', 'Other', 'Refund (Grant Item)', 'Refund (Non-grant Item)'];

  // Update categories when transaction type changes
  useEffect(() => {
    if (transactionType === 'expenses') {
      setCategories(expenseCategories);
    } else if (transactionType === 'deposits') {
      setCategories(depositCategories);
    } else {
      // Combine arrays and remove duplicates
      const combinedCategories = [...expenseCategories, ...depositCategories];
      const uniqueCategories = [...new Set(combinedCategories)];
      setCategories(uniqueCategories);
    }
  }, [transactionType]);

  const fetchCustomCategories = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/customized-categories`, {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      setCustomCategories(data);
    } catch (error) {
      console.error('Error fetching custom categories:', error);
    }
  }, []);

  const fetchGrantList = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grants/grant-list`, {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const grantData = await response.json();
      setGrantList(grantData);
    } catch (error) {
      console.error('Error fetching grant list:', error);
    }
  }, []);

  // Fetch all confirmed transactions from all sources
  const fetchAllTransactions = useCallback(async () => {
    try {
      const dataPromises = [
        // Debit card expenses
        fetch(`${process.env.REACT_APP_BACKEND_API_URL}/expense`, {
          credentials: 'include'
        })
          .then(response => response.ok ? response.json() : [])
          .then(data => data.map(item => ({ 
            ...item, 
            SourceType: 'Debit Card',
            Bank: item['Bank Type'] || 'Unknown',
            AccountNumber: item['Last Four Digits'] ? `****${item['Last Four Digits']}` : 'N/A',
            AccountType: 'Debit',
            TransactionCategory: parseFloat(item.Amount) > 0 ? 'deposit' : 'expense'
          }))),
        
        // Debit card deposits (positive amounts from expense table)
        fetch(`${process.env.REACT_APP_BACKEND_API_URL}/deposit`, {
          credentials: 'include'
        })
          .then(response => response.ok ? response.json() : [])
          .then(data => data.map(item => ({ 
            ...item, 
            SourceType: 'Debit Card',
            Bank: item['Bank Type'] || 'Unknown',
            AccountNumber: 'N/A',
            AccountType: 'Debit',
            TransactionCategory: 'deposit'
          }))),

        // Credit card expenses
        fetch(`${process.env.REACT_APP_BACKEND_API_URL}/credit-card/expense`, {
          credentials: 'include'
        })
          .then(response => response.ok ? response.json() : [])
          .then(data => data.map(item => ({ 
            ...item, 
            SourceType: 'Credit Card',
            Bank: item['Bank Type'] || 'Unknown',
            AccountNumber: item['Last Four Digits'] ? `****${item['Last Four Digits']}` : 'N/A',
            AccountType: 'Credit',
            TransactionCategory: 'expense'
          }))),

        // Credit card deposits
        fetch(`${process.env.REACT_APP_BACKEND_API_URL}/credit-card/deposits`, {
          credentials: 'include'
        })
          .then(response => response.ok ? response.json() : [])
          .then(data => data.map(item => ({ 
            ...item, 
            SourceType: 'Credit Card',
            Bank: item['Bank Type'] || 'Unknown',
            AccountNumber: item['Last Four Digits'] ? `****${item['Last Four Digits']}` : 'N/A',
            AccountType: 'Credit',
            TransactionCategory: 'deposit'
          }))),

        // Savings account transactions
        fetch(`${process.env.REACT_APP_BACKEND_API_URL}/savings`, {
          credentials: 'include'
        })
          .then(response => response.ok ? response.json() : [])
          .then(data => data.map(item => ({ 
            ...item, 
            SourceType: 'Savings Account',
            Bank: item['Bank Type'] || 'Unknown',
            AccountNumber: item['Account Number'] || 'N/A',
            AccountType: 'Savings',
            TransactionCategory: parseFloat(item.Amount) > 0 ? 'deposit' : 'expense'
          }))),

        // Confirmed payroll transactions
        fetch(`${process.env.REACT_APP_BACKEND_API_URL}/payroll/confirmed-transactions`, {
          credentials: 'include'
        })
          .then(response => response.ok ? response.json() : [])
          .then(data => data.map(item => ({
            ...item,
            SourceType: 'Payroll',
            Bank: 'Rippling',
            AccountNumber: 'N/A',
            AccountType: 'Payroll',
            TransactionCategory: 'expense'
          })))
      ];

      // Wait for all requests to complete
      const resultsArray = await Promise.all(dataPromises);
      
      // Merge and sort the results
      const mergedData = [].concat(...resultsArray);
      const sortedData = mergedData.sort(
        (a, b) => new Date(b["Date (UTC)"]) - new Date(a["Date (UTC)"])
      );
      
      setTransactions(sortedData);
      
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  }, []);

  useEffect(() => {
    fetchAllTransactions();
    fetchGrantList();
    fetchCustomCategories();
  }, [fetchAllTransactions, fetchGrantList, fetchCustomCategories]);

  // Filter transactions
  useEffect(() => {
    let filtered = transactions;

    // Filter for confirmed transactions only
    filtered = filtered.filter((t) => t.Checked === "Yes");

    // Transaction type filter
    if (transactionType !== 'all') {
      if (transactionType === 'expenses') {
        filtered = filtered.filter(t => t.TransactionCategory === 'expense' && t.SourceType !== 'Payroll');
      } else if (transactionType === 'deposits') {
        filtered = filtered.filter(t => t.TransactionCategory === 'deposit');
      } else if (transactionType === 'payroll') {
        filtered = filtered.filter(t => t.SourceType === 'Payroll');
      }
    }

    // Period filtering with default to last 30 days
    const currentDate = new Date();
    let startDate, endDate;

    switch (expensePeriod) {
      case "all":
        break;
      case "last30Days":
        startDate = new Date(currentDate.getTime() - (30 * 24 * 60 * 60 * 1000));
        endDate = currentDate;
        break;
      case "currentYear":
        startDate = new Date(currentDate.getFullYear(), 0, 1); 
        endDate = new Date(currentDate.getFullYear(), 11, 31);
        break;
      case "lastYear":
        startDate = new Date(currentDate.getFullYear() - 1, 0, 1);
        endDate = new Date(currentDate.getFullYear() - 1, 11, 31);
        break;
      case "lastQuarter":
        startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 3, 1);
        endDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
        break;
      case "lastMonth":
        startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
        endDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
        break;
      case "custom":
        startDate = new Date(customStartDate);
        endDate = new Date(customEndDate);
        break;
      default:
        if (!isNaN(parseInt(expensePeriod))) {
          startDate = new Date(parseInt(expensePeriod), 0, 1);
          endDate = new Date(parseInt(expensePeriod), 11, 31);
        }
    }

    if (startDate && endDate) {
      filtered = filtered.filter((t) => {
        const transactionDate = new Date(t["Date (UTC)"]);
        return transactionDate >= startDate && transactionDate <= endDate;
      });
    }

    // Category filtering
    if (grantCategory !== "all") {
      filtered = filtered.filter((t) => {
        if (t.TransactionCategory === 'deposit') {
          return t["Deposit Category"] === grantCategory;
        } else {
          return t["Grant Category"] === grantCategory;
        }
      });
    }

    // Grant filtering
    if (selectedGrantFilter !== "all") {
      filtered = filtered.filter((t) => {
        try {
          const defaultPayment = JSON.parse(t["Default Payment"] || "{}");
          return defaultPayment[selectedGrantFilter] > 0;
        } catch (e) {
          return false;
        }
      });
    }

    setFilteredTransactions(filtered);
  }, [transactions, transactionType, expensePeriod, customStartDate, customEndDate, grantCategory, selectedGrantFilter]);

  const getExpensePeriodOptions = () => {
    return [
      { value: "last30Days", label: "Last 30 Days" },
      { value: "all", label: "All" },
      { value: "currentYear", label: "Current Year" },
      { value: "lastYear", label: "Last Year" },
      { value: "lastQuarter", label: "Last Quarter" },
      { value: "lastMonth", label: "Last Month" },
      { value: "custom", label: "Custom Period" },
    ];
  };

  const exportToCSV = () => {
    if (filteredTransactions.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = [
      'Date (UTC)',
      'Amount',
      'Bank',
      'Account Number',
      'Account Type',
      'Bank Description',
      'Category',
      'Customized Category',
      'Payment (%)',
      'Note'
    ];

    const csvContent = [
      headers.join(','),
      ...filteredTransactions.map(transaction => [
        `"${new Date(transaction["Date (UTC)"]).toLocaleDateString()}"`,
        `"$${Math.abs(parseFloat(transaction.Amount)).toFixed(2)}"`,
        `"${transaction.Bank}"`,
        `"${transaction.AccountNumber}"`,
        `"${transaction.AccountType}"`,
        `"${transaction["Bank Description"] || ''}"`,
        `"${transaction["Grant Category"] || transaction["Deposit Category"] || ''}"`,
        `"${transaction["Customized Category"] || ''}"`,
        `"${Object.entries(JSON.parse(transaction["Default Payment"] || "{}"))
          .map(([source, percentage]) => `${source}: ${percentage}%`)
          .join(", ")}"`,
        `"${transaction.Note || ''}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `confirmed-transactions-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleEditCategories = (transaction) => {
    setEditingTransaction(transaction);
    setEditedGrantCategory(transaction["Grant Category"] || transaction["Deposit Category"] || "");
    setEditedCustomizedCategory(transaction["Customized Category"] || "");
  };

  const handleSaveCategories = async () => {
    if (!editingTransaction) return;

    try {
      // Determine the endpoint based on the transaction source and type
      let endpoint = "";
      let categoryType = "";
      
      if (editingTransaction.TransactionCategory === "expense") {
        if (editingTransaction.SourceType === "Credit Card") {
          endpoint = `credit-card/${editingTransaction.ID}`;
        } else if (editingTransaction.SourceType === "Savings Account") {
          endpoint = `savings/${editingTransaction.ID}`;
        } else {
          endpoint = `expense/${editingTransaction.ID}`;
        }
        categoryType = "Grant Category";
      } else {
        if (editingTransaction.SourceType === "Credit Card") {
          endpoint = `credit-card/deposit/${editingTransaction.ID}`;
        } else if (editingTransaction.SourceType === "Savings Account") {
          endpoint = `savings/${editingTransaction.ID}`;
        } else {
          endpoint = `deposit/${editingTransaction.ID}`;
        }
        categoryType = "Deposit Category";
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/${endpoint}`, {
        method: "PUT",
        credentials: 'include',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          [categoryType]: editedGrantCategory,
          "Customized Category": editedCustomizedCategory,
        }),
      });

      if (response.ok) {
        // Update transactions in state
        const updatedTransactions = filteredTransactions.map((t) =>
          t.ID === editingTransaction.ID
            ? {
                ...t,
                [categoryType]: editedGrantCategory,
                "Customized Category": editedCustomizedCategory,
              }
            : t
        );
        setFilteredTransactions(updatedTransactions);
        
        // Also update the main transactions list
        const updatedAllTransactions = transactions.map((t) =>
          t.ID === editingTransaction.ID
            ? {
                ...t,
                [categoryType]: editedGrantCategory,
                "Customized Category": editedCustomizedCategory,
              }
            : t
        );
        setTransactions(updatedAllTransactions);
        
        setEditingTransaction(null);
      } else {
        const errorText = await response.text();
        alert(`Failed to update categories: ${errorText}`);
      }
    } catch (error) {
      console.error("Error updating categories:", error);
      alert(`Error updating categories: ${error.message}`);
    }
  };

  const handleEditDefaultPaymentClick = (transaction) => {
    let defaultPayment = transaction["Default Payment"];
    if (typeof defaultPayment === "string") {
      try {
        defaultPayment = JSON.parse(defaultPayment);
      } catch (error) {
        defaultPayment = {};
      }
    }
    setSelectedTransaction({
      ...transaction,
      defaultPayment: defaultPayment,
    });
    setShowEditDefaultPaymentPopup(true);
  };

  const handleEditDefaultPaymentSubmit = async (selectedTransaction) => {
    if (!selectedTransaction) {
      alert('No transaction selected');
      return;
    }
  
    try {
      let endpoint = "";
      if (selectedTransaction.TransactionCategory === "expense") {
        if (selectedTransaction.SourceType === "Credit Card") {
          endpoint = `credit-card/expense/${selectedTransaction.ID}`;
        } else if (selectedTransaction.SourceType === "Savings Account") {
          endpoint = `savings/${selectedTransaction.ID}`;
        } else {
          endpoint = `expense/${selectedTransaction.ID}`;
        }
      } else {
        if (selectedTransaction.SourceType === "Credit Card") {
          endpoint = `credit-card/deposit/${selectedTransaction.ID}`;
        } else if (selectedTransaction.SourceType === "Savings Account") {
          endpoint = `savings/${selectedTransaction.ID}`;
        } else {
          endpoint = `deposit/${selectedTransaction.ID}`;
        }
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/${endpoint}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          'Default Payment': JSON.stringify(selectedTransaction.defaultPayment),
        }),
      });
  
      if (response.ok) {
        const updatedTransactions = filteredTransactions.map(t =>
          t.ID === selectedTransaction.ID
            ? { ...t, 'Default Payment': JSON.stringify(selectedTransaction.defaultPayment) }
            : t
        );
        setFilteredTransactions(updatedTransactions);
        setShowEditDefaultPaymentPopup(false);
      } else {
        const errorText = await response.text();
        alert(`Failed to update default payment: ${errorText}`);
      }
    } catch (error) {
      console.error('Error updating default payment:', error);
      alert(`Error updating default payment: ${error.message}`);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h1 className="text-2xl font-bold text-gray-900">Confirmed Transactions</h1>
          <button onClick={exportToCSV} className="btn-primary">
            Export CSV
          </button>
        </div>
        <div className="mt-4 flex flex-wrap gap-4 items-end">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Transaction Type</label>
            <select
              value={transactionType}
              onChange={(e) => setTransactionType(e.target.value)}
              className="input-field min-w-[160px]"
            >
              <option value="all">All Transactions</option>
              <option value="expenses">Expenses Only</option>
              <option value="deposits">Deposits Only</option>
              <option value="payroll">Payroll Only</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Grant</label>
            <select
              value={selectedGrantFilter}
              onChange={(e) => setSelectedGrantFilter(e.target.value)}
              className="input-field min-w-[160px]"
            >
              <option value="all">All Transactions</option>
              {grantList.map((grant) => (
                <option key={grant} value={grant}>{grant}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Period</label>
            <select
              value={expensePeriod}
              onChange={(e) => {
                const selectedPeriod = e.target.value;
                setExpensePeriod(selectedPeriod);
                if (selectedPeriod === "custom") {
                  setShowCustomPeriodPopup(true);
                }
              }}
              className="input-field min-w-[160px]"
            >
              {getExpensePeriodOptions().map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium text-gray-700">Category</label>
            <select
              value={grantCategory}
              onChange={(e) => setGrantCategory(e.target.value)}
              className="input-field min-w-[160px]"
            >
              <option value="all">All</option>
              {categories.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4 text-sm text-gray-600">
          Showing {filteredTransactions.length} confirmed transactions
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-auto" style={{ maxHeight: '70vh' }}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-30">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date (UTC)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bank</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account #</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customized Category</th>
                <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" colSpan={Math.max(grantList.length, 1)}>Payment (%)</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
              <tr>
                <th className="px-6 py-2 bg-gray-50"></th>
                <th className="px-6 py-2 bg-gray-50"></th>
                <th className="px-6 py-2 bg-gray-50"></th>
                <th className="px-6 py-2 bg-gray-50"></th>
                <th className="px-6 py-2 bg-gray-50"></th>
                <th className="px-6 py-2 bg-gray-50"></th>
                <th className="px-6 py-2 bg-gray-50"></th>
                <th className="px-6 py-2 bg-gray-50"></th>
                {grantList.length > 0 ? grantList.map((grant) => (
                  <th key={grant} className="px-2 py-2 text-center text-xs font-medium text-gray-700 bg-gray-100 border-l border-gray-200">{grant}</th>
                )) : (
                  <th className="px-2 py-2 bg-gray-50"></th>
                )}
                <th className="px-6 py-2 bg-gray-50"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredTransactions.map((transaction) => {
                const lineItems = getLineItems(transaction);
                const isItemized = lineItems.length > 0;
                const isEditing = editingTransaction && editingTransaction.ID === transaction.ID;
                const isExpense = transaction.TransactionCategory === 'expense';
                const amountColor = isExpense ? 'text-red-600' : 'text-green-600';
                const paymentObj = (() => {
                  try { return JSON.parse(transaction["Default Payment"] || "{}"); } catch { return {}; }
                })();
                const totalCols = 8 + Math.max(grantList.length, 1) + 1; // 8 base + grant subcols + actions
                return (
                  <React.Fragment key={transaction.ID}>
                    <tr className={`hover:bg-gray-50 transition-colors duration-150 ${isEditing ? 'bg-primary-50 border-l-4 border-primary-400' : ''}`}>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900">
                        {new Date(transaction["Date (UTC)"]).toLocaleDateString()}
                      </td>
                      <td className={`px-6 py-3 whitespace-nowrap text-sm font-medium ${amountColor}`}>
                        ${Math.abs(parseFloat(transaction.Amount)).toFixed(2)}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${isExpense ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                          {isExpense ? 'Expense' : 'Deposit'}
                        </span>
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">{transaction.Bank}</td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">{transaction.AccountNumber}</td>
                      <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700">{transaction.AccountType}</td>
                      <td className="px-6 py-3 whitespace-nowrap">
                        {isEditing ? (
                          <select
                            value={editedGrantCategory}
                            onChange={(e) => setEditedGrantCategory(e.target.value)}
                            className="input-field text-sm min-w-[140px]"
                          >
                            <option value="">-- Select Category --</option>
                            {(transaction.TransactionCategory === 'expense' ? expenseCategories : depositCategories).map((category) => (
                              <option key={category} value={category}>{category}</option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-sm text-gray-900">
                            {transaction["Grant Category"] || transaction["Deposit Category"] || <span className="text-gray-400">—</span>}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-3 whitespace-nowrap">
                        {isEditing ? (
                          <select
                            value={editedCustomizedCategory}
                            onChange={(e) => setEditedCustomizedCategory(e.target.value)}
                            className="input-field text-sm min-w-[120px]"
                          >
                            <option value="">-- Select Category --</option>
                            {customCategories.map((category) => (
                              <option key={category} value={category}>{category}</option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-sm text-gray-900">
                            {transaction["Customized Category"] || <span className="text-gray-400">—</span>}
                          </span>
                        )}
                      </td>
                      {isItemized ? (
                        <td colSpan={Math.max(grantList.length, 1)} className="px-6 py-3 text-sm text-gray-900">
                          <button
                            onClick={() => setExpandedItemRows(prev => ({ ...prev, [transaction.ID]: !prev[transaction.ID] }))}
                            className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors duration-200"
                          >
                            {expandedItemRows[transaction.ID] ? `Hide Items (${lineItems.length})` : `Show Items (${lineItems.length})`}
                          </button>
                        </td>
                      ) : (
                        grantList.length > 0 ? grantList.map((grant) => {
                          const pct = paymentObj[grant];
                          return (
                            <td key={grant} className="px-2 py-3 text-center text-sm text-gray-900 border-l border-gray-100">
                              {pct !== undefined && pct !== null && pct !== '' ? `${pct}%` : <span className="text-gray-300">—</span>}
                            </td>
                          );
                        }) : (
                          <td className="px-2 py-3 text-center text-sm text-gray-400">—</td>
                        )
                      )}
                      <td className="px-6 py-3 whitespace-nowrap">
                        <div className="flex gap-2">
                          {isEditing ? (
                            <>
                              <button
                                onClick={handleSaveCategories}
                                className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors duration-200"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => setEditingTransaction(null)}
                                className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors duration-200"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleEditCategories(transaction)}
                                className="px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100 transition-colors duration-200"
                              >
                                Edit Categories
                              </button>
                              <button
                                onClick={() => handleEditDefaultPaymentClick(transaction)}
                                className="px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md hover:bg-amber-100 transition-colors duration-200"
                              >
                                Edit Payment
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    {transaction["Bank Description"] && (
                      <tr className={`${isEditing ? 'bg-primary-50' : 'bg-gray-50'}`}>
                        <td colSpan={totalCols} className="px-6 py-1 text-xs text-gray-600 italic border-b border-gray-200">
                          <span className="font-medium text-gray-500 uppercase tracking-wider mr-2">Bank:</span>
                          {transaction["Bank Description"]}
                        </td>
                      </tr>
                    )}
                    {isItemized && expandedItemRows[transaction.ID] && lineItems.map((item, idx) => (
                      <tr key={`${transaction.ID}-item-${item.id}`} className="bg-blue-50 border-b border-blue-100">
                        <td></td>
                        <td className="pl-6 py-2 text-xs text-gray-600">
                          ${Math.abs(parseFloat(item.amount) || 0).toFixed(2)}
                        </td>
                        <td colSpan={2} className="text-xs text-gray-600">
                          {item.name || `Item #${idx + 1}`}
                        </td>
                        <td colSpan={2}></td>
                        <td className="text-xs text-gray-600">{item.grantCategory || item.depositCategory || ''}</td>
                        <td className="text-xs text-gray-600">{item.customizedCategory || ''}</td>
                        <td colSpan={Math.max(grantList.length, 1)} className="text-xs text-gray-600">
                          {Object.entries(item.payment || {})
                            .filter(([, v]) => parseFloat(v) > 0)
                            .map(([source, percentage]) => `${source}: ${percentage}%`)
                            .join(", ")}
                        </td>
                        <td></td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
              {filteredTransactions.length === 0 && (
                <tr>
                  <td colSpan={9 + Math.max(grantList.length, 1)} className="px-6 py-10 text-center text-sm text-gray-500">
                    No confirmed transactions to display.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showEditDefaultPaymentPopup && selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fade-in">
          <EditDefaultPaymentPopup
            selectedTransaction={selectedTransaction}
            grantList={grantList}
            onSave={handleEditDefaultPaymentSubmit}
            onCancel={() => setShowEditDefaultPaymentPopup(false)}
          />
        </div>
      )}

      {showCustomPeriodPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="card p-6 max-w-md mx-auto animate-slide-up">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Custom Period</h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Start Date</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="input-field"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">End Date</label>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="input-field"
                />
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowCustomPeriodPopup(false);
                    setExpensePeriod("custom");
                  }}
                  className="btn-primary flex-1"
                >
                  Apply
                </button>
                <button
                  onClick={() => setShowCustomPeriodPopup(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EditDefaultPaymentPopup = ({
  selectedTransaction,
  grantList,
  onSave,
  onCancel,
}) => {
  const [defaultPayment, setDefaultPayment] = useState(
    typeof selectedTransaction.defaultPayment === "string"
      ? JSON.parse(selectedTransaction.defaultPayment || "{}")
      : selectedTransaction.defaultPayment || {}
  );

  const handlePercentageChange = (grant, percentage) => {
    setDefaultPayment((prev) => ({
      ...prev,
      [grant]: percentage,
    }));
  };

  const handleSubmit = () => {
    const totalPercentage = Object.values(defaultPayment).reduce(
      (sum, value) => sum + parseFloat(value || 0),
      0
    );

    if (Math.abs(totalPercentage - 100) > 0.01) {
      alert(
        `Total percentage must equal 100%. Current total: ${totalPercentage.toFixed(
          2
        )}%`
      );
      return;
    }
    onSave({ ...selectedTransaction, defaultPayment });
  };

  return (
    <div className="card p-6 max-w-md mx-auto animate-slide-up">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Edit Payment</h3>
      <div className="space-y-4 max-h-[60vh] overflow-y-auto">
        {grantList.map((grant) => (
          <div key={grant} className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">{grant}</label>
            <input
              type="number"
              value={defaultPayment[grant] || ""}
              onChange={(e) => handlePercentageChange(grant, e.target.value)}
              min="0"
              max="100"
              step="0.01"
              className="input-field"
              placeholder="0.00"
            />
          </div>
        ))}
      </div>
      <div className="flex gap-3 mt-6">
        <button onClick={handleSubmit} className="btn-primary flex-1">Save</button>
        <button onClick={onCancel} className="btn-secondary flex-1">Cancel</button>
      </div>
    </div>
  );
};

export default ConfirmedTransactions;
